import {
  HTTP_STATUS_MESSAGES,
  NETWORK_ERROR_MESSAGE,
} from "constants/apiErrorMessages";
import urlMapping from "constants/urlMapping";
import {
  buildRedirectTarget,
  getLoginPathWithRedirect,
  logout,
} from "helpers/authSession";
import { camelizeKeys, tokenUtils } from "utils";

const isLoginRequestUrl = (requestUrl) =>
  String(requestUrl).includes(urlMapping.login);

const extractErrorMessage = (response, json) => {
  if (!json) {
    return HTTP_STATUS_MESSAGES[response.status] || "An unknown error occurred";
  }

  if (Array.isArray(json.detail)) {
    return json.detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return String(item);

        const fieldPath = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== "body").join(".")
          : "";
        const message = item.msg || "Unknown error";

        return fieldPath ? `${fieldPath}: ${message}` : message;
      })
      .join(", ");
  }

  if (typeof json.detail === "string") return json.detail;
  if (json.message) return json.message;

  return HTTP_STATUS_MESSAGES[response.status] || "An unknown error occurred";
};

const fetchJSON = async (url, options = {}) => {
  const token = tokenUtils.getToken();

  const newOptions = {
    method: options.method,
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  if (options.body) {
    if (options.body instanceof FormData) {
      delete newOptions.headers["Content-Type"];
      newOptions.body = options.body;
    } else {
      newOptions.body = JSON.stringify(options.body);
    }
  }

  try {
    const response = await fetch(url, newOptions);
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      if (response.status === 401 && !isLoginRequestUrl(url)) {
        logout();
        window.location.href = getLoginPathWithRedirect(
          buildRedirectTarget(
            window.location.pathname,
            window.location.search
          )
        );
        const authError = new Error(HTTP_STATUS_MESSAGES[response.status] || "Unauthorized");
        authError.statusCode = response.status;
        authError.statusText = response.statusText;
        authError.data = json || null;
        throw authError;
      }

      const message = extractErrorMessage(response, json);
      const apiError = new Error(message);
      apiError.statusCode = response.status;
      apiError.statusText = response.statusText;
      apiError.data = json || null;
      throw apiError;
    }

    return {
      statusCode: response.status,
      statusText: response.statusText,
      ...camelizeKeys(json || {}),
    };
  } catch (error) {
    if (error?.statusCode) {
      throw error;
    }

    const networkError = new Error(error?.message || NETWORK_ERROR_MESSAGE);
    networkError.statusCode = 0;
    networkError.data = null;
    throw networkError;
  }
};

export default fetchJSON;
